import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, Mail, Lock, User, ArrowRight } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ code: "", email: "", username: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.token, data.user);
      nav("/");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-tv-cyan bg-tv-cyan/10 border border-tv-cyan/25 rounded-full px-3 py-1 mb-4">
        <KeyRound size={13} /> Invitation only
      </div>
      <h2 className="text-2xl font-extrabold mb-1" data-testid="register-title">
        Redeem your access code
      </h2>
      <p className="text-tv-sub text-sm mb-7">
        Registration is closed. Enter a valid one-time code to create your account.
      </p>

      {err && (
        <div className="mb-4 text-sm text-tv-red bg-tv-red/10 border border-tv-red/30 rounded-xl px-4 py-3" data-testid="register-error">
          {err}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-tv-sub mb-1.5 block">Access code</label>
          <div className="relative">
            <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-tv-muted" />
            <input
              required
              value={form.code}
              onChange={set("code")}
              placeholder="XXXXXXXX"
              className="field pl-10 uppercase tracking-widest font-semibold"
              data-testid="register-code-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-tv-sub mb-1.5 block">Email</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-tv-muted" />
            <input type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className="field pl-10" data-testid="register-email-input" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-tv-sub mb-1.5 block">Username</label>
          <div className="relative">
            <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-tv-muted" />
            <input required value={form.username} onChange={set("username")} placeholder="Your name" className="field pl-10" data-testid="register-username-input" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-tv-sub mb-1.5 block">Password</label>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-tv-muted" />
            <input type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="At least 6 characters" className="field pl-10" data-testid="register-password-input" />
          </div>
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="register-submit-button">
          {loading ? <span className="spinner" /> : <ArrowRight size={18} />}
          {loading ? "Creating account…" : "Create account"}
        </button>
      </form>

      <p className="text-center text-sm text-tv-sub mt-6">
        Already have an account?{" "}
        <Link to="/login" className="text-tv-cyan font-semibold hover:underline" data-testid="goto-login">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
